// Global packages and components
import { useState, useEffect, useCallback } from 'react'
import { useFetcher } from '@remix-run/react'
import { mapSearchBrendaVariant } from '~/utils'
import { action as variantsAction } from '~/routes/api.get-products'

// Types
import type { ProductSearchItem, BrendaSearchProductsResponse } from '~/types'

interface QueryProps {
  searchTerm?: string | null
  limit?: string
  page?: string | null
  onSale?: string | null
  orderBy?: string | null
  categorySlug?: string
  categoryFilters?: string[]
  productType?: string
  productCodes?: string[]
  attributes?: string[] | null
  priceRange?: string | null
  returnFilters?: boolean
}

/*
  Client side product fetch, now used only by the product carousel blocks.

  The listing pages resolve their products in the route loader instead, so the
  parent product branch, the facet and total state, and the price seeding that
  used to drive the sidebar have all gone with them.
*/
const useProducts = () => {
  const fetcher = useFetcher<typeof variantsAction>()

  const [products, setProducts] = useState<ProductSearchItem[]>([])

  // Derived rather than passed in and mirrored back
  const loading = fetcher.state !== 'idle'

  const getProducts = useCallback(
    async ({
      searchTerm,
      limit,
      page,
      onSale,
      orderBy,
      categorySlug,
      categoryFilters,
      productType,
      productCodes,
      attributes,
      priceRange,
      returnFilters,
    }: QueryProps = {}) => {
      const options = {
        searchTerm: searchTerm ?? '*',
        limit: limit ?? '9',
        page: page ?? '1',
        onSale: onSale == 'true' ? 'true' : '',
        orderBy: orderBy ?? 'RELEVANCE',
        categorySlug: categorySlug,
        categoryFilters: categoryFilters,
        productType: productType,
        productCodes: productCodes ?? [],
        attributes: attributes && attributes?.length > 0 ? attributes : [],
        priceRange: priceRange,
        returnFilters: returnFilters,
      }

      fetcher.submit(
        {
          options: JSON.stringify(options),
        },
        {
          method: 'post',
          encType: 'application/json',
          action: '/api/get-products',
        }
      )
    },
    // @NOTE: ONLY USE THE BELOW TO SURPRESS FETCHER WARNINGS
    // ..IF YOUR SURE THE FETCHER ISNT CHANGING BETWEEN RENDERS
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (fetcher?.state == 'loading' && fetcher?.data) {
      const listing = fetcher?.data
        ?.listing as BrendaSearchProductsResponse['body']

      if (listing) {
        setProducts(
          listing?.entities[0]?.records?.length > 0
            ? listing?.entities[0]?.records.map(e => mapSearchBrendaVariant(e))
            : []
        )
      }
    }
  }, [fetcher])

  return {
    loading,
    products,
    getProducts,
  }
}

export default useProducts
